import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from 'recharts';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

// Mock service cost data
const mockServiceData = {
  breakdown: [
    { name: 'OpenAI GPT-4', cost: 847.32, percentage: 42.3, color: '#10b981', trend: 8 },
    { name: 'Anthropic Claude', cost: 423.18, percentage: 21.1, color: '#3b82f6', trend: -5 },
    { name: 'Vapi Voice', cost: 312.45, percentage: 15.6, color: '#8b5cf6', trend: 12 },
    { name: 'OpenRouter', cost: 287.91, percentage: 14.4, color: '#f59e0b', trend: -2 },
    { name: 'Other Services', cost: 132.67, percentage: 6.6, color: '#6b7280', trend: 0 }
  ],
  timeline: [
    { date: '2024-01-20', openai: 145.32, anthropic: 67.89, vapi: 45.21, openrouter: 34.56 },
    { date: '2024-01-21', openai: 156.78, anthropic: 71.23, vapi: 48.90, openrouter: 38.12 },
    { date: '2024-01-22', openai: 134.56, anthropic: 65.45, vapi: 42.34, openrouter: 31.89 },
    { date: '2024-01-23', openai: 167.89, anthropic: 78.90, vapi: 52.13, openrouter: 41.23 },
    { date: '2024-01-24', openai: 142.98, anthropic: 69.12, vapi: 46.78, openrouter: 35.67 }
  ],
  efficiency: [
    { service: 'OpenAI GPT-4', requestsPerDollar: 425, avgResponseTime: 1.2, successRate: 99.2 },
    { service: 'Anthropic Claude', requestsPerDollar: 612, avgResponseTime: 0.8, successRate: 99.8 },
    { service: 'Vapi Voice', requestsPerDollar: 89, avgResponseTime: 2.1, successRate: 97.5 },
    { service: 'OpenRouter', requestsPerDollar: 378, avgResponseTime: 1.5, successRate: 98.9 }
  ]
};

interface ServiceMetricCardProps {
  service: {
    name: string;
    cost: number;
    percentage: number;
    color: string;
    trend: number;
  };
}

function ServiceMetricCard({ service }: ServiceMetricCardProps) {
  const getTrendIcon = () => {
    if (service.trend > 0) return <TrendingUp className="w-3 h-3 text-red-500" />;
    if (service.trend < 0) return <TrendingDown className="w-3 h-3 text-green-500" />;
    return <Minus className="w-3 h-3 text-gray-400" />;
  };

  const getTrendColor = () => {
    if (service.trend > 0) return 'text-red-600';
    if (service.trend < 0) return 'text-green-600';
    return 'text-gray-400';
  };

  return (
    <Card className="hover:shadow-sm transition-shadow">
      <CardContent className="p-4">
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div 
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: service.color }}
              />
              <span className="font-medium text-sm">{service.name}</span>
            </div>
            <Badge variant="outline" className="text-xs">
              {service.percentage}%
            </Badge>
          </div>
          
          <div className="space-y-1">
            <div className="text-2xl font-bold">${service.cost.toFixed(2)}</div>
            <div className="flex items-center gap-1 text-xs">
              {getTrendIcon()}
              <span className={getTrendColor()}>
                {service.trend > 0 ? '+' : ''}{service.trend}% vs last week
              </span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

interface ServiceEfficiencyTableProps {
  data: Array<{
    service: string;
    requestsPerDollar: number;
    avgResponseTime: number;
    successRate: number;
  }>;
}

function ServiceEfficiencyTable({ data }: ServiceEfficiencyTableProps) {
  const getEfficiencyColor = (value: number, type: 'requests' | 'response' | 'success') => {
    switch (type) {
      case 'requests':
        if (value > 500) return 'text-green-600';
        if (value > 300) return 'text-yellow-600';
        return 'text-red-600';
      case 'response':
        if (value < 1.0) return 'text-green-600';
        if (value < 2.0) return 'text-yellow-600';
        return 'text-red-600';
      case 'success':
        if (value > 99) return 'text-green-600';
        if (value > 97) return 'text-yellow-600';
        return 'text-red-600';
    }
  };

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b">
            <th className="text-left p-2 font-medium">Service</th>
            <th className="text-right p-2 font-medium">Requests/$</th>
            <th className="text-right p-2 font-medium">Avg Response (s)</th>
            <th className="text-right p-2 font-medium">Success Rate</th>
            <th className="text-right p-2 font-medium">Efficiency Score</th>
          </tr>
        </thead>
        <tbody>
          {data.map((row) => {
            // Simple efficiency score calculation
            const efficiencyScore = Math.round(
              (row.requestsPerDollar / 10) + 
              (100 / row.avgResponseTime) + 
              row.successRate
            ) / 3;

            return (
              <tr key={row.service} className="border-b hover:bg-gray-50">
                <td className="p-2 font-medium">{row.service}</td>
                <td className={`p-2 text-right font-mono ${getEfficiencyColor(row.requestsPerDollar, 'requests')}`}>
                  {row.requestsPerDollar}
                </td>
                <td className={`p-2 text-right font-mono ${getEfficiencyColor(row.avgResponseTime, 'response')}`}>
                  {row.avgResponseTime.toFixed(1)}s
                </td>
                <td className={`p-2 text-right font-mono ${getEfficiencyColor(row.successRate, 'success')}`}>
                  {row.successRate}%
                </td>
                <td className="p-2 text-right font-mono font-bold">
                  {efficiencyScore.toFixed(1)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export function ServiceCostBreakdown() {
  const data = mockServiceData;
  
  return (
    <div className="space-y-6">
      {/* Service Cost Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        {data.breakdown.map((service) => (
          <ServiceMetricCard key={service.name} service={service} />
        ))}
      </div>

      {/* Visual Breakdown */}
      <div className="grid grid-cols-2 gap-6">
        {/* Pie Chart */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Cost Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={data.breakdown}
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="cost"
                  label={({ name, percentage }) => `${name}: ${percentage}%`}
                  labelLine={false}
                >
                  {data.breakdown.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip 
                  formatter={(value: number) => [`$${value.toFixed(2)}`, 'Cost']}
                  contentStyle={{ 
                    backgroundColor: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px'
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Cost Timeline */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Daily Cost Trend</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={data.timeline}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis 
                  dataKey="date" 
                  tick={{ fontSize: 10 }}
                  stroke="#6b7280"
                  tickFormatter={(value) => new Date(value).getDate().toString()}
                />
                <YAxis 
                  tick={{ fontSize: 10 }}
                  stroke="#6b7280"
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px'
                  }}
                  formatter={(value: number) => [`$${value.toFixed(2)}`, '']}
                  labelFormatter={(value) => new Date(value).toLocaleDateString()}
                />
                <Line type="monotone" dataKey="openai" stroke="#10b981" strokeWidth={2} name="OpenAI" />
                <Line type="monotone" dataKey="anthropic" stroke="#3b82f6" strokeWidth={2} name="Anthropic" />
                <Line type="monotone" dataKey="vapi" stroke="#8b5cf6" strokeWidth={2} name="Vapi" />
                <Line type="monotone" dataKey="openrouter" stroke="#f59e0b" strokeWidth={2} name="OpenRouter" />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Service Efficiency Analysis */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Service Efficiency Analysis</CardTitle>
          <div className="text-sm text-muted-foreground">
            Performance and cost efficiency metrics per service
          </div>
        </CardHeader>
        <CardContent>
          <ServiceEfficiencyTable data={data.efficiency} />
        </CardContent>
      </Card>

      {/* Cost per Request Comparison */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Cost per Request Comparison</CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart 
              data={data.efficiency.map(item => ({
                service: item.service.replace(/.*?(\w+)$/, '$1'), // Get last word for shorter labels
                costPerRequest: (1 / item.requestsPerDollar).toFixed(4)
              }))}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis 
                dataKey="service" 
                tick={{ fontSize: 10 }}
                stroke="#6b7280"
              />
              <YAxis 
                tick={{ fontSize: 10 }}
                stroke="#6b7280"
                label={{ value: 'Cost per Request ($)', angle: -90, position: 'insideLeft' }}
              />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'white',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px'
                }}
                formatter={(value: string) => [`$${value}`, 'Cost per Request']}
              />
              <Bar dataKey="costPerRequest" fill="#3b82f6" />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    </div>
  );
}